"use client"

import { useState, useContext, useEffect } from "react"
import { X, Calendar, Clock, User, Stethoscope, FileText } from "lucide-react"
import { createAppointment } from "../../../services/appointmentApi"
import { getPetsByOwner } from "../../../services/api"
import { AuthContext } from "../../../context/AuthContext"

const CreateAppointmentModal = ({ isOpen, onClose, onSuccess, onError, selectedPet }) => {
  const { loginUser } = useContext(AuthContext)

  const [loading, setLoading] = useState(false)
  const [pets, setPets] = useState([])
  const [loadingPets, setLoadingPets] = useState(false)

  // Form state
  const [appointmentForm, setAppointmentForm] = useState({
    petId: "",
    type: "",
    date: "",
    time: "",
    doctor: "",
    status: "pending",
    notes: "",
  })

  // Appointment types
  const appointmentTypes = [
    "checkup",
    "vaccination",
    "surgery",
    "consultation",
    "emergency",
    "grooming",
    "dental",
    "follow-up",
  ]

  // Available doctors
  const doctors = ["Dr. Smith", "Dr. Johnson", "Dr. Brown", "Dr. Wilson", "Dr. Davis", "Dr. Miller"]

  // Time slots
  const timeSlots = [
    "08:00",
    "08:30",
    "09:00",
    "09:30",
    "10:00",
    "10:30",
    "11:00",
    "11:30",
    "12:00",
    "12:30",
    "13:00",
    "13:30",
    "14:00",
    "14:30",
    "15:00",
    "15:30",
    "16:00",
    "16:30",
    "17:00",
    "17:30",
    "18:00",
  ]

  // Load pets when modal opens
  useEffect(() => {
    if (isOpen && loginUser?.id) {
      loadPets()
    }
  }, [isOpen, loginUser?.id])

  // Set selected pet when provided
  useEffect(() => {
    if (selectedPet && isOpen) {
      setAppointmentForm((prev) => ({
        ...prev,
        petId: selectedPet._id,
      }))
    }
  }, [selectedPet, isOpen])

  const loadPets = async () => {
    try {
      setLoadingPets(true)
      const response = await getPetsByOwner(loginUser.id)
      if (response.success) {
        setPets(response.data || [])
      }
    } catch (error) {
      console.error("Error loading pets:", error)
      onError("Failed to load pets")
    } finally {
      setLoadingPets(false)
    }
  }

  const resetForm = () => {
    setAppointmentForm({
      petId: "",
      type: "",
      date: "",
      time: "",
      doctor: "",
      status: "pending",
      notes: "",
    })
  }

  const handleClose = () => {
    resetForm()
    onClose()
  }

  // Get today's date in YYYY-MM-DD format
  const getTodayDate = () => {
    return new Date().toISOString().split("T")[0]
  }

  const handleSubmit = async () => {
    // Validate form
    if (
      !appointmentForm.petId ||
      !appointmentForm.type ||
      !appointmentForm.date ||
      !appointmentForm.time ||
      !appointmentForm.doctor
    ) {
      onError("Please fill in all required fields")
      return
    }

    // Validate date is not in the past
    const selectedDate = new Date(appointmentForm.date)
    const today = new Date()
    today.setHours(0, 0, 0, 0)

    if (selectedDate < today) {
      onError("Appointment date cannot be in the past")
      return
    }

    try {
      setLoading(true)

      const appointmentData = {
        pets: appointmentForm.petId,
        type: appointmentForm.type,
        date: appointmentForm.date,
        time: appointmentForm.time,
        doctor: appointmentForm.doctor,
        status: appointmentForm.status,
        notes: appointmentForm.notes,
      }

      const response = await createAppointment(appointmentData)

      if (response.success) {
        resetForm()
        onSuccess("Appointment booked successfully!")
        onClose()
      } else {
        onError(response.message || "Failed to create appointment")
      }
    } catch (err) {
      onError(err.message || "Failed to create appointment")
    } finally {
      setLoading(false)
    }
  }

  // Get selected pet details for display
  const selectedPetDetails = pets.find((pet) => pet._id === appointmentForm.petId)

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Book Appointment</h2>
              <p className="text-gray-600 mt-1">Schedule a new appointment for your pet</p>
            </div>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
              disabled={loading}
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Pet Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <User className="w-4 h-4 inline mr-1" />
                  Select Pet *
                </label>
                <select
                  value={appointmentForm.petId}
                  onChange={(e) => setAppointmentForm({ ...appointmentForm, petId: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  disabled={loading || loadingPets}
                  required
                >
                  <option value="">Select a pet</option>
                  {pets.map((pet) => (
                    <option key={pet._id} value={pet._id}>
                      {pet.name} ({pet.species} - {pet.breed})
                    </option>
                  ))}
                </select>
              </div>

              {/* Appointment Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Stethoscope className="w-4 h-4 inline mr-1" />
                  Appointment Type *
                </label>
                <select
                  value={appointmentForm.type}
                  onChange={(e) => setAppointmentForm({ ...appointmentForm, type: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  disabled={loading}
                  required
                >
                  <option value="">Select appointment type</option>
                  {appointmentTypes.map((type) => (
                    <option key={type} value={type}>
                      {type.charAt(0).toUpperCase() + type.slice(1)}
                    </option>
                  ))}
                </select>
              </div>

              {/* Date */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Calendar className="w-4 h-4 inline mr-1" />
                  Date *
                </label>
                <input
                  type="date"
                  value={appointmentForm.date}
                  onChange={(e) => setAppointmentForm({ ...appointmentForm, date: e.target.value })}
                  min={getTodayDate()}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  disabled={loading}
                  required
                />
              </div>

              {/* Time */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Clock className="w-4 h-4 inline mr-1" />
                  Time *
                </label>
                <select
                  value={appointmentForm.time}
                  onChange={(e) => setAppointmentForm({ ...appointmentForm, time: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  disabled={loading}
                  required
                >
                  <option value="">Select time</option>
                  {timeSlots.map((time) => (
                    <option key={time} value={time}>
                      {time}
                    </option>
                  ))}
                </select>
              </div>

              {/* Doctor */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <User className="w-4 h-4 inline mr-1" />
                  Doctor *
                </label>
                <select
                  value={appointmentForm.doctor}
                  onChange={(e) => setAppointmentForm({ ...appointmentForm, doctor: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  disabled={loading}
                  required
                >
                  <option value="">Select doctor</option>
                  {doctors.map((doctor) => (
                    <option key={doctor} value={doctor}>
                      {doctor}
                    </option>
                  ))}
                </select>
              </div>

              {/* Status */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                <select
                  value={appointmentForm.status}
                  onChange={(e) => setAppointmentForm({ ...appointmentForm, status: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  disabled={loading}
                >
                  <option value="pending">Pending</option>
                  <option value="confirmed">Confirmed</option>
                  <option value="cancelled">Cancelled</option>
                  <option value="completed">Completed</option>
                </select>
              </div>

              {/* Notes */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <FileText className="w-4 h-4 inline mr-1" />
                  Notes
                </label>
                <textarea
                  value={appointmentForm.notes}
                  onChange={(e) => setAppointmentForm({ ...appointmentForm, notes: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Add any additional notes about the appointment..."
                  disabled={loading}
                />
              </div>
            </div>

            {/* Selected Pet Info */}
            {selectedPetDetails && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-4">
                <h3 className="font-medium text-blue-900 mb-2">Selected Pet Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-blue-800">
                  <div>
                    <span className="font-medium">Name:</span> {selectedPetDetails.name}
                  </div>
                  <div>
                    <span className="font-medium">Species:</span> {selectedPetDetails.species}
                  </div>
                  <div>
                    <span className="font-medium">Breed:</span> {selectedPetDetails.breed}
                  </div>
                  <div>
                    <span className="font-medium">Age:</span> {selectedPetDetails.age} years
                  </div>
                  <div>
                    <span className="font-medium">Weight:</span> {selectedPetDetails.weight}kg
                  </div>
                  <div>
                    <span className="font-medium">Gender:</span> {selectedPetDetails.gender}
                  </div>
                </div>
              </div>
            )}

            <div className="flex space-x-4 pt-4">
              <button
                onClick={handleClose}
                disabled={loading}
                className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-700 py-2 px-4 rounded-lg transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={loading || loadingPets}
                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-lg transition-colors disabled:opacity-50"
              >
                {loading ? "Booking..." : "Book Appointment"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default CreateAppointmentModal
